<div class="wrapper">
    <section class="flex justify-between items-center bg-white border-2 border-b-indigo-800">
        <h2 class="top-div-header" matDialogTitle>
            Create new sale
        </h2>
        <button type="button" mat-icon-button (click)="close()" title="Close dialog">
            <mat-icon fontIcon="close" aria-label="close icon"></mat-icon>
        </button>
    </section>

    <section body matDialogContent>
        <div class="sales-form" [formGroup]="saleFormGroup">

            <mat-form-field>
                <mat-label>Item name</mat-label>
                <input formControlName="item_name" autocomplete="off" matInput placeholder="Bucket" [maxlength]="itemNameMaxLength">
                <mat-error *ngIf="showError('item_name', 'required')">Give the item a name</mat-error>
                <mat-error *ngIf="showError('item_name', 'minlength')">
                    At least {{ saleFormGroup.get('item_name')?.errors?.minlength?.requiredLength }} characters
                </mat-error>
            </mat-form-field>

            <mat-form-field>
                <mat-label>Price</mat-label>
                <span matTextPrefix>&#8358;&nbsp;</span>
                <input type="text" inputmode="numeric" formControlName="price" matInput placeholder="2,000">
                <mat-error *ngIf="showError('price', 'required')">Enter a price</mat-error>
                <mat-error *ngIf="showError('price', 'notANumber')">Use numbers only, like 2,000</mat-error>
                <mat-error *ngIf="showError('price', 'min')">The price has to be at least &#8358;1</mat-error>
            </mat-form-field>

            <mat-checkbox formControlName="is_item_negotiable" class="example-margin">Negotiable</mat-checkbox>

            <mat-form-field *ngIf="saleFormGroup.get('is_item_negotiable')?.value">
                <mat-label>Lowest price you'll take</mat-label>
                <span matTextPrefix>&#8358;&nbsp;</span>
                <input type="text" inputmode="numeric" formControlName="minimum_price" matInput placeholder="1,500">
                <!-- Maybe later pre-fill with AI predicted value -->
                <mat-hint>Buyers don't see this</mat-hint>
                <mat-error *ngIf="showError('minimum_price', 'required')">Enter the lowest price you'll take, or untick Negotiable</mat-error>
                <mat-error *ngIf="showError('minimum_price', 'notANumber')">Use numbers only, like 1,500</mat-error>
                <mat-error *ngIf="showError('minimum_price', 'min')">It has to be at least &#8358;1</mat-error>
                <mat-error *ngIf="showError('minimum_price', 'notMoreThan')">
                    It can't be more than the price (&#8358;{{ saleFormGroup.get('minimum_price')?.errors?.notMoreThan?.max | number }})
                </mat-error>
            </mat-form-field>

            <!-- Photos: required (buyers skip listings without one), square previews - that's how the card
                 crops them - and a tap picks the cover. -->
            <fieldset class="m-0 border-0 p-0">
                <legend class="mb-1 text-sm font-medium text-gray-900">Photos <span *ngIf="!isAdminPost" class="text-gray-500">(at least one)</span></legend>
                <p class="m-0 mb-3 text-xs text-gray-500">Tap a photo to make it the cover - it's what buyers see first. Photos are shown square.</p>

                <div class="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    <div *ngFor="let photo of photos; let i = index" class="relative aspect-square overflow-hidden rounded-lg ring-1 ring-black/10" [class.!ring-2]="i === coverIndex" [class.!ring-primary]="i === coverIndex">
                        <button type="button" class="block h-full w-full p-0" (click)="setCover(i)"
                            [attr.aria-label]="i === coverIndex ? 'Photo ' + (i + 1) + ', the cover' : 'Make photo ' + (i + 1) + ' the cover'"
                            [attr.aria-pressed]="i === coverIndex">
                            <img [src]="photo.dataUrl" alt="" class="h-full w-full object-cover" />
                        </button>
                        <span *ngIf="i === coverIndex" class="pointer-events-none absolute bottom-1 left-1 rounded bg-primary px-1.5 py-0.5 text-[11px] font-semibold text-accent">Cover</span>
                        <button type="button" class="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-red-700 shadow-sm"
                            (click)="removePhoto(i)" [attr.aria-label]="'Remove photo ' + (i + 1)">
                            <mat-icon class="!h-auto !w-auto !text-lg">close</mat-icon>
                        </button>
                    </div>

                    <label *ngIf="photos.length < maxPhotos"
                        class="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed text-sm text-primary transition hover:bg-primary/5 focus-within:outline focus-within:outline-2 focus-within:outline-primary"
                        [class.border-primary]="!photoMissing" [class.border-red-600]="photoMissing" [attr.data-invalid]="photoMissing || null">
                        <mat-icon aria-hidden="true">add_a_photo</mat-icon>
                        {{ photos.length ? 'Add more' : 'Add photos' }}
                        <!-- TODO: accept video too ("image/*,video/*", and the filter in onFileSelected) once video
                             uploads go to YouTube or a similar video service - we need video support. Until then
                             photos only: a raw video can't be a card's cover or be previewed here. -->
                        <input #imgFileInput type="file" accept="image/*" multiple class="sr-only" (change)="onFileSelected($event)"
                            [attr.aria-invalid]="photoMissing || null" [attr.aria-describedby]="photoMissing ? 'sale-photo-error' : null" />
                    </label>
                </div>
                <p *ngIf="photoMissing" id="sale-photo-error" matError class="m-0 mt-2 text-xs" role="alert">Add at least one photo of the item.</p>
            </fieldset>

            <div>
                <p id="sale-condition-label" class="m-0 mb-2 text-sm font-medium text-gray-900">Condition</p>
                <mat-button-toggle-group formControlName="condition" aria-labelledby="sale-condition-label" class="flex-wrap"
                    [class.toggle-group-invalid]="showError('condition')">
                    <mat-button-toggle *ngFor="let condition of conditions" [value]="condition.value">{{ condition.label }}</mat-button-toggle>
                </mat-button-toggle-group>
                <p *ngIf="showError('condition', 'required')" matError class="m-0 mt-2 text-xs" role="alert">
                    Choose the item's condition
                </p>
            </div>

            <mat-form-field>
                <mat-label>Category</mat-label>
                <mat-select formControlName="category">
                    <mat-option *ngFor="let category of categories" [value]="category.value">{{ category.label }}</mat-option>
                </mat-select>
                <mat-error *ngIf="showError('category', 'required')">Choose a category</mat-error>
            </mat-form-field>

            <mat-form-field>
                <mat-label>Item description</mat-label>
                <textarea title="description" rows="3"
                cdkAutosizeMinRows="3"
                cdkAutosizeMaxRows="5" formControlName="text" matInput placeholder=""></textarea>
                <mat-hint>Optional</mat-hint>
                <!-- TODO: textbox should expand as you type -->
              </mat-form-field>
        </div>

        <div class="flex justify-between items-center pt-6">
            <app-progress-button (click)="submit()" buttonText="Post" [loading]="submittingSale"></app-progress-button>
            <button type="button" mat-stroked-button (click)="saveDraft()">Save as draft</button>
        </div>
    </section>
</div>
